import produce from 'immer';
import { create } from 'zustand';

import type { FullTextSearchResult } from 'api/data/entities/projects/search/FullTextSearchResult';

interface FullTextSearchStateProps {
  // state for the input box
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;

  isFocused: boolean;
  isLoading: boolean;

  setIsFocused: (isFocused: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;

  results: FullTextSearchResult[];
  setResults: (results: FullTextSearchResult[], regexMatch: string[]) => void;

  // what was used to call the api
  regexMatch: string[];

  // local filters
  onlyShowHitProperties: boolean;
  toggleOnlyShowHitProperties: () => void;

  // excludedNodeTypes
  filteredNodeTypes: string[];
  addFilteredNodeType: (type: string) => void;
  removeFilteredNodeType: (type: string) => void;

  selectedItemId: string;
  selectedItemTemplateNodeId: string;

  setSelectedItem: (itemId: string, selectedItemTemplateNodeId: string) => void;
}

export const FULLTEXT_SEARCH_STATE = create<FullTextSearchStateProps>((set) => ({
  searchTerm: '',

  isFocused: false,
  isLoading: false,

  results: [],
  foundLabels: [],

  regexMatch: [],
  onlyShowHitProperties: true,

  filteredNodeTypes: [],

  selectedItemId: '',
  selectedItemTemplateNodeId: '',

  addFilteredNodeType: (typeToExclude: string) =>
    set((state) =>
      produce(state, (draft) => {
        if (!state.filteredNodeTypes.includes(typeToExclude)) {
          draft.filteredNodeTypes.push(typeToExclude);
        }
      })
    ),

  removeFilteredNodeType: (typeToExclude: string) =>
    set((state) =>
      produce(state, (draft) => {
        const existingIndex = state.filteredNodeTypes.indexOf(typeToExclude);

        if (existingIndex !== -1) {
          draft.filteredNodeTypes.splice(existingIndex, 1);
        }
      })
    ),

  setIsFocused: (isFocused: boolean) =>
    set((state) =>
      produce(state, (draft) => {
        draft.isFocused = isFocused;
      })
    ),

  setIsLoading: (isLoading: boolean) =>
    set((state) =>
      produce(state, (draft) => {
        draft.isLoading = isLoading;
      })
    ),

  setSearchTerm: (term: string) =>
    set((state) =>
      produce(state, (draft) => {
        draft.searchTerm = term;
      })
    ),

  setSelectedItem: (itemId, selectedItemTemplateNodeId) =>
    set((state) =>
      produce(state, (draft) => {
        draft.selectedItemId = itemId;
        draft.selectedItemTemplateNodeId = selectedItemTemplateNodeId;
      })
    ),

  setResults: (results: FullTextSearchResult[], regexMatch: string[]) =>
    set((state) =>
      produce(state, (draft) => {
        draft.results = results;
        draft.regexMatch = regexMatch;

        // the type filter is carried over searched, but we only show chips for the found types.
        // If there is no overlap, we need to remove the filter or the results will be blank
        // and there is no way to remove the filter because the chip for the filter won't be displayed
        state.filteredNodeTypes.forEach((filter) => {
          const isAppliccable = results.some((n) => {
            const searchStr = n.Label.split('_')[1];
            if (!searchStr) {
              return false;
            }

            return filter.includes(searchStr);
          });

          if (!isAppliccable) {
            const index = state.filteredNodeTypes.indexOf(filter);
            draft.filteredNodeTypes.splice(index, 1);
          }
        });
      })
    ),

  toggleOnlyShowHitProperties: () =>
    set((state) =>
      produce(state, (draft) => {
        draft.onlyShowHitProperties = !state.onlyShowHitProperties;
      })
    )
}));
